import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { canManageUnion } from '../../../shared/domain/unionAccess.js';
import { useRaidOverview } from '../../raid-overview/hooks/useRaidOverview.js';
import { loadCurrentUnionRoster } from '../../union-roster/api/unionRosterApi.js';
import { composeUnionMembers } from '../../union-roster/domain/composeUnionMembers.js';
import {
  loadRaidSettlementCorrectionStats,
  loadRaidSettlementHistory,
  saveRaidSettlementComment,
} from '../api/raidSettlementApi.js';
import { buildSettlementLevelHistory } from '../domain/raidSettlementLevels.js';

const EMPTY_HISTORY = { seasons: [], records: [] };

export function useRaidSettlement() {
  const overview = useRaidOverview();
  const [historyRows, setHistoryRows] = useState(EMPTY_HISTORY);
  const [rosterMembers, setRosterMembers] = useState([]);
  const [correctionStats, setCorrectionStats] = useState(null);
  const [correctionError, setCorrectionError] = useState('');
  const [insightError, setInsightError] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentNotice, setCommentNotice] = useState({ type: '', message: '' });
  const runRef = useRef(0);
  const correctionRunRef = useRef(0);

  useEffect(() => {
    if (!overview.unionId) {
      setHistoryRows(EMPTY_HISTORY);
      setRosterMembers([]);
      return undefined;
    }

    let active = true;
    let loadSequence = 0;
    const runId = runRef.current + 1;
    runRef.current = runId;

    const load = async ({ forceRoster = false } = {}) => {
      const sequence = loadSequence + 1;
      loadSequence = sequence;
      try {
        const [history, roster] = await Promise.all([
          loadRaidSettlementHistory(overview.unionId),
          loadCurrentUnionRoster({ force: forceRoster }).catch((error) => {
            if (String(error?.code || '') === 'roster_migration_required') return { members: [] };
            throw error;
          }),
        ]);
        if (!active || runRef.current !== runId || sequence !== loadSequence) return;
        setHistoryRows(history);
        setRosterMembers(Array.isArray(roster?.members) ? roster.members : []);
        setInsightError('');
      } catch (error) {
        if (!active || runRef.current !== runId || sequence !== loadSequence) return;
        setHistoryRows(EMPTY_HISTORY);
        setRosterMembers([]);
        setInsightError(error instanceof Error ? error.message : '결산 추가 데이터를 불러오지 못했습니다.');
      }
    };

    load();
    const reload = () => load();
    const reloadRoster = () => load({ forceRoster: true });
    window.addEventListener('dororaid:union-roster-changed', reloadRoster);
    window.addEventListener('dororaid:raid-overview-changed', reload);
    window.addEventListener('dororaid:raid-season-changed', reload);

    return () => {
      active = false;
      runRef.current += 1;
      window.removeEventListener('dororaid:union-roster-changed', reloadRoster);
      window.removeEventListener('dororaid:raid-overview-changed', reload);
      window.removeEventListener('dororaid:raid-season-changed', reload);
    };
  }, [overview.unionId]);

  const selectedSeason = useMemo(() => {
    if (!overview.selectedSeason) return null;
    const records = Array.isArray(overview.selectedSeason.records) ? overview.selectedSeason.records : [];
    const bosses = (Array.isArray(overview.selectedSeason.bosses) ? overview.selectedSeason.bosses : [])
      .map((boss) => ({ ...boss, settlementCorrectionStats: correctionStats }));
    return {
      ...overview.selectedSeason,
      bosses,
      members: composeUnionMembers({
        rosterMembers,
        fallbackMembers: overview.selectedSeason.members || [],
        records,
        maxMembers: 64,
      }),
    };
  }, [correctionStats, overview.selectedSeason, rosterMembers]);

  useEffect(() => {
    const seasonNo = Number(selectedSeason?.seasonNo || 0);
    if (!overview.unionId || !seasonNo) {
      setCorrectionStats(null);
      setCorrectionError('');
      return undefined;
    }

    let active = true;
    const runId = correctionRunRef.current + 1;
    correctionRunRef.current = runId;
    const load = async () => {
      try {
        const next = await loadRaidSettlementCorrectionStats(seasonNo);
        if (!active || correctionRunRef.current !== runId) return;
        setCorrectionStats(next);
        setCorrectionError('');
      } catch (error) {
        if (!active || correctionRunRef.current !== runId) return;
        setCorrectionStats(null);
        setCorrectionError(error instanceof Error ? error.message : '전체 유니온 보정 통계를 불러오지 못했습니다.');
      }
    };

    load();
    const reload = () => load();
    window.addEventListener('dororaid:raid-overview-changed', reload);
    window.addEventListener('dororaid:raid-season-changed', reload);

    return () => {
      active = false;
      correctionRunRef.current += 1;
      window.removeEventListener('dororaid:raid-overview-changed', reload);
      window.removeEventListener('dororaid:raid-season-changed', reload);
    };
  }, [overview.unionId, selectedSeason?.seasonNo]);

  const levelHistory = useMemo(() => buildSettlementLevelHistory({
    seasons: historyRows.seasons,
    records: historyRows.records,
    members: rosterMembers,
    selectedSeasonNo: selectedSeason?.seasonNo,
    currentMembersOnly: true,
  }), [historyRows, rosterMembers, selectedSeason?.seasonNo]);

  const saveComment = useCallback(async (comment) => {
    if (!selectedSeason?.seasonNo || commentSaving) return false;
    setCommentSaving(true);
    setCommentNotice({ type: '', message: '' });
    try {
      await saveRaidSettlementComment({ seasonNo: selectedSeason.seasonNo, comment });
      await overview.refresh(selectedSeason.seasonNo);
      setCommentNotice({ type: 'success', message: '결산 코멘트를 저장했습니다.' });
      return true;
    } catch (error) {
      setCommentNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '결산 코멘트를 저장하지 못했습니다.',
      });
      return false;
    } finally {
      setCommentSaving(false);
    }
  }, [commentSaving, overview.refresh, selectedSeason?.seasonNo]);

  return {
    ...overview,
    selectedSeason,
    correctionStats,
    correctionError,
    levelHistory,
    insightError,
    canManageSettlement: canManageUnion(overview.viewerRole),
    commentSaving,
    commentNotice,
    clearCommentNotice: () => setCommentNotice({ type: '', message: '' }),
    saveComment,
  };
}
